<script setup>
import { useStore } from 'vuex'
import ActionBar from '@/components/common/ActionBar.vue'
import DataField from '@/components/form/DataField.vue'
import ComboBoxDataField from '@/components/form/ComboBoxDataField.vue'
import { computed, onMounted } from 'vue'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'
import { ADMIN_ROLE, CONTRIBUTOR_ROLE } from '@/components/constant'

const { addDirty, getDirtyClass, dirtyItems } = dirtyFieldMixin()

const store = useStore()
const currentItem = computed(() => store.state.multiplePhreaticLine.currentItem)
const currentSchematisationStage = computed(() => store.state.stabSchematisationStage.currentItem)
const isLocked = computed(() => store.getters['stabSchematisation/isLockedCurrent'])
const schematisationStagesToImport = computed(
  () => store.getters['stabSchematisationStage/previousSchematisationStagesHaveHeadLine']
)

const currentWaterLevel = computed(
  () => currentSchematisationStage.value?.stage_configuration?.phreatic_line
)
const riverWaterLevelOptions = computed(() => {
  const options = currentItem.value?.river_water_level_options ?? []
  return options.filter((option) => option.water_level === currentWaterLevel.value)
})
const polderWaterLevelOptions = computed(() => {
  const options = currentItem.value?.polder_water_level_options ?? []
  if (currentWaterLevel.value === 'extreme') {
    return options.filter((option) => option.water_level === 'high')
  }
  return options.filter((option) => option.water_level === currentWaterLevel.value)
})

const update = () => store.dispatch('multiplePhreaticLine/update')
const setNestedParameter = (payload) =>
  store.dispatch('multiplePhreaticLine/setNestedParameter', payload)
const emptyDirtyItems = () => store.dispatch('multiplePhreaticLine/emptyDirtyItems')
const toggleImportDialog = (value) =>
  store.dispatch('multiplePhreaticLine/toggleImportDialog', value)

const currentRole = computed(() => store.getters['project/getCurrentRole'])

const riverWaterLevelAutoSelect = () => {
  if (
    riverWaterLevelOptions.value.length > 0 &&
    currentItem.value.river_water_level === null &&
    [ADMIN_ROLE, CONTRIBUTOR_ROLE].includes(currentRole.value)
  ) {
    setNestedParameter({
      item: currentItem.value,
      parameter: 'river_water_level',
      value: riverWaterLevelOptions.value[0]?.value
    })
    addDirty(currentItem.value, 'river_water_level')
  }
}
const polderWaterLevelAutoSelect = () => {
  if (
    polderWaterLevelOptions.value.length > 0 &&
    currentItem.value.polder_water_level === null &&
    [ADMIN_ROLE, CONTRIBUTOR_ROLE].includes(currentRole.value)
  ) {
    setNestedParameter({
      item: currentItem.value,
      parameter: 'polder_water_level',
      value: polderWaterLevelOptions.value[0]?.value
    })
    addDirty(currentItem.value, 'polder_water_level')
  }
}
const saveChanges = async () => {
  try {
    await update()
  } catch (e) {
    console.error(e)
  }
  emptyDirtyItems()
  dirtyItems.value = []
}
onMounted(() => {
  riverWaterLevelAutoSelect()
  polderWaterLevelAutoSelect()
})

defineExpose({ dirtyItems })
</script>

<template>
  <div>
    <ActionBar
      class="pa-3"
      show-import
      show-save
      :disabled="isLocked"
      @save="saveChanges()"
      :disabled-import="schematisationStagesToImport.length === 0"
      @import="toggleImportDialog(true)"
    />

    <div class="pa-3 pt-0 fill-height overflow-y-auto">
      <ComboBoxDataField
        label="River water level [m+NAP]"
        @set="setNestedParameter"
        @update="addDirty(currentItem, 'river_water_level')"
        :classField="[
          getDirtyClass(currentItem, 'river_water_level'),
          isLocked ? 'disable-events' : ''
        ]"
        parameter="river_water_level"
        :item="currentItem"
        :options="riverWaterLevelOptions"
      ></ComboBoxDataField>

      <ComboBoxDataField
        class="mt-1"
        label="Polder water level [m+NAP]"
        @set="setNestedParameter"
        @update="addDirty(currentItem, 'polder_water_level')"
        :classField="[
          getDirtyClass(currentItem, 'polder_water_level'),
          isLocked ? 'disable-events' : ''
        ]"
        parameter="polder_water_level"
        :item="currentItem"
        :options="polderWaterLevelOptions"
      ></ComboBoxDataField>
      <DataField
        label="Depth dry torn zone [m]"
        @update="addDirty(currentItem, 'depth_dry_torn_zone')"
        @set="setNestedParameter"
        density="default"
        :classField="[
          getDirtyClass(currentItem, 'depth_dry_torn_zone'),
          isLocked ? 'disable-events' : ''
        ]"
        class="mt-1"
        parameter="depth_dry_torn_zone"
        :item="currentItem"
      >
      </DataField>
    </div>
  </div>
</template>
